import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { TooltipProvider } from './components/ui/tooltip';
import { Provider } from 'react-redux';
import store from './store/store';
import { Buffer } from 'buffer';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { AppToaster } from './components/cms/ui/app-toaster';
import { useNavigate, useLocation, useParams } from 'react-router-dom';


import { ModalProvider } from './modules/modalService/ModalProvider';
import CacheBuster from './modules/versionCacheBuster/cacheBuster';
import { ServiceMonitorBoot, ServiceGate } from './modules/serviceMonitor';

import { ProtectedRoute } from './common/ProtectedRoute';
import { SuperuserProtectedRoute } from './common/SuperuserProtectedRoute';
import { AdminProtectedRoute } from './common/AdminProtectedRoute';
import { AuthProvider } from './hooks/AuthProvider';
import { OrganizationProvider } from './hooks/useOrganization';
import { Login } from './pages/landing/Login';
import { Signup } from './pages/landing/Signup';
import { PasswordResetConfirm } from './pages/landing/PasswordResetConfirm';

// import pages
// 3D-heavy pages are lazy-loaded so three.js/drei stay out of the initial bundle
const EditorPage = lazy(loadEditorPage);
import Dashboard from './pages/cms/Dashboard';
import MyAccount from './pages/cms/MyAccount';
import ProjectDetail from './pages/cms/ProjectDetail';
import ProjectEdit from './pages/cms/ProjectEdit';
import StoryEdit from './pages/cms/StoryEdit';
import BillingSuccess from './pages/billing/BillingSuccess';
import BillingCancel from './pages/billing/BillingCancel';
import OpenInApp from './pages/OpenInApp';
import ConnectApprove from './pages/connect/ConnectApprove';
import { forgetReturnPath, peekReturnPath } from './modules/auth/returnAfterLogin';
import ShareLanding from './pages/ShareLanding';
import NotFound from './pages/cms/NotFound';
import CreatorLanding from './pages/marketing/CreatorLanding';
const MarketplacePage = lazy(() => import('./pages/marketplace/MarketplacePage'));
const MarketplaceProductPage = lazy(() => import('./pages/marketplace/MarketplaceProductPage'));
const AssetUploadPage = lazy(() => import('./pages/admin/AssetUploadPage'));
const MessagePage = lazy(() => import('./pages/quickMessage/MessagePage'));
const DesignSystemPage = lazy(() => import('./pages/designSystem/DesignSystemPage'));
import { EnvironmentPreferencesProvider } from './hooks/EnvironmentPreferencesProvider';
import { ServiceProvider, useServices } from './infrastructure/providers/ServiceProvider';
import type { Services } from './infrastructure/services';
import { usePageTracking } from './hooks/usePageTracking';
import { PostHogAnalyticsBinder, PostHogConfigurator, OAuthAnalyticsTracker, AttributionSync } from './modules/analytics';
import { ThemeAccountSync, ThemeCycleKeys, type ThemeAccount } from './modules/theme';
import { followAccountChanges, onAccountChanges } from '@/modules/mcpStream';
import { invalidateMemberPreferences } from './infrastructure/adapters/memberPreferencesCache';
import { currentUserId } from './infrastructure/clients/BackendClient';
import { useAuthUser } from './hooks/useAuthUser';
import { reloadRemoteConfig } from './modules/remoteConfig';
import { useStartModelDecoders } from './modules/compressedModels/assetCompression';
import { ThemeRowsSync } from './modules/themePanel/ThemeRowsSync';
import { BACKEND_ENVIRONMENT } from './infrastructure/clients/backendEnv';
import { CookieBanner, CookiePreferencesModal } from './modules/cookieConsent';
import { loadEditorPage, prefetchEditorPage } from './pages/editor/loadEditorPage';
import { EditorLoadingScreen } from './components/editor/EditorLoadingScreen';

if (!window.Buffer) {
  window.Buffer = Buffer;
}

// Unity Reload Handler - handles navigation after Unity corruption reload
function UnityReloadHandler() {
  const navigate = useNavigate();
  
  useEffect(() => {
    const targetUrl = sessionStorage.getItem('unity-reload-target');
    if (targetUrl) {
      console.log('[UnityReloadHandler] Navigating to stored target after reload:', targetUrl);
      sessionStorage.removeItem('unity-reload-target');
      navigate(targetUrl);
    }
  }, [navigate]);
  
  return null;
}

// Scroll Restoration Handler - resets scroll on route change.
// useLayoutEffect runs synchronously before the browser paints the new page,
// ensuring body.scrollTop is zeroed before EditorPage measures its layout.
// window.scrollTo resets the html-level scroll; body.scrollTop resets the body
// scroll container (used when body.overflow:auto is set by CMS pages).
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
  }, [pathname]);
  
  return null;
}

// Redirect that keeps the query string so UTM params from marketing
// links survive until PostHog reads them on the destination page.
function NavigateKeepSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

// Signed in at the root (an OAuth sign-in lands here): back to the page login was asked for, else the dashboard.
function NavigateHome() {
  const [returnTo] = useState(() => peekReturnPath(currentUserId()));
  useEffect(forgetReturnPath, []);
  return returnTo ? <Navigate to={returnTo} replace /> : <NavigateKeepSearch to="/dashboard" />;
}

// Legacy plural routes (/projects/...) redirect to the singular shape decided in
// lureo-docs#348. Emails, Slack cards and bookmarks in the wild still carry the
// plural paths, so these stay for as long as anything can link to them.
function RedirectLegacyRoute({ to }: { to: (params: Record<string, string | undefined>) => string }) {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={{ pathname: to(params), search }} replace />;
}

// Component to handle routes
function AppRoutes() {
  // Track page views in Google Analytics
  usePageTracking();
  const { pathname } = useLocation();
  // Every page that opens a story fetches the editor's code ahead of the click
  useEffect(() => {
    if (/^\/(dashboard|project|story)(\/|$)/.test(pathname)) prefetchEditorPage();
  }, [pathname]);

  return (
    <>
      <ScrollToTop />
      <UnityReloadHandler />
      <Suspense fallback={null}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/creators" element={<CreatorLanding />} />
        <Route path="/reset-password" element={<PasswordResetConfirm />} />
        <Route path="/" element={<ProtectedRoute element={<NavigateHome />} />} />
      <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
      <Route path="/account" element={<ProtectedRoute element={<MyAccount />} />} />
      <Route path="/project/:id" element={<ProtectedRoute element={<ProjectDetail />} />} />
      <Route path="/project/edit/:id" element={<ProtectedRoute element={<ProjectEdit />} />} />
      <Route path="/story/edit/:storyId" element={<ProtectedRoute element={<StoryEdit />} />} />
      <Route path="/projects/:id" element={<RedirectLegacyRoute to={(p) => `/project/${p.id}`} />} />
      <Route path="/projects/:id/edit" element={<RedirectLegacyRoute to={(p) => `/project/edit/${p.id}`} />} />
      <Route path="/projects/:projectId/stories/:storyId/edit" element={<RedirectLegacyRoute to={(p) => `/story/edit/${p.storyId}`} />} />
      {/* Its own boundary, so the splash shows the moment a story is opened instead of the last page waiting on the editor's code */}
      <Route
        path={`/editor/:sceneId`}
        element={
          <ProtectedRoute
            element={
              <Suspense fallback={<EditorLoadingScreen isLoaded={false} sceneLoaded={false} isSceneReady={false} orgLoaded={false} codeLoaded={false} />}>
                <ServiceGate>
                  <EditorPage />
                </ServiceGate>
              </Suspense>
            }
          />
        }
      />
      <Route path="/message" element={<ProtectedRoute element={<MessagePage />} />} />
      {/* The design system site, for the team: production shows not-found */}
      {/* The design system, its themes and their hotkeys are for the team on staging and local only; production has none of them */}
      {BACKEND_ENVIRONMENT !== 'production' && <Route path="/design-system" element={<ProtectedRoute element={<DesignSystemPage />} />} />}
      <Route path="/marketplace" element={<SuperuserProtectedRoute element={<MarketplacePage />} />} />
      <Route path="/marketplace/:productId" element={<SuperuserProtectedRoute element={<MarketplaceProductPage />} />} />
      <Route path="/admin/asset-upload" element={<AdminProtectedRoute element={<AssetUploadPage />} />} />
      {/* Billing Routes - Not protected since users return from external Stripe checkout */}
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing/cancel" element={<BillingCancel />} />
      {/* App deep-link fallback - not protected; renders only when the native app didn't intercept /open */}
      <Route path="/open" element={<OpenInApp />} />
      <Route path="/connect/approve" element={<ProtectedRoute returnAfterLogin element={<ConnectApprove />} />} />
      {/* Share-link landing - public by design: a stranger scanning a poster has no account (share links 3.1) */}
      <Route path="/s/:slug" element={<ShareLanding />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
      </Suspense>
    </>
  );
}

// The signed-in user's account change stream, on every page, and what is shared across pages: the signed-in user, the
// membership's cached preferences, and the libraries, which read again as after a save of their own
function AccountHost() {
  const { user, loading, refreshUser } = useAuthUser();
  const userId = user?.id;

  // The first remote config read goes out with the stored session before auth resolves, so it is for that user already
  const settingsFor = useRef(currentUserId());
  useEffect(() => {
    if (loading) return;
    const id = userId ?? null;
    if (settingsFor.current === id) return;
    settingsFor.current = id;
    reloadRemoteConfig();
  }, [userId, loading]);

  // With the asset_compression flag on, the model decoders start before the first model needs them
  useStartModelDecoders();

  useEffect(() => {
    if (!userId) return;
    const stop = followAccountChanges();
    const unsubscribe = [
      // The user's name, email or avatar changed elsewhere: every page shows them as they are now
      onAccountChanges(['user'], () => void refreshUser()),
      onAccountChanges(['orgMember'], events => {
        if (events.length === 0 || events.some(event => event.entityUniqueId === userId)) invalidateMemberPreferences(userId);
      }, 0),
      onAccountChanges(['assetType', 'assetFile', 'image'], () => window.dispatchEvent(new CustomEvent('libraryRefetchNeeded'))),
      onAccountChanges(['uiElementType'], () => window.dispatchEvent(new CustomEvent('screenUILibraryRefetchNeeded'))),
    ];
    return () => {
      unsubscribe.forEach(stopListening => stopListening());
      stop();
    };
  }, [userId, refreshUser]);

  return null;
}

// The theme on the signed-in member's preferences (member_preferences.theme); a change made elsewhere (Claude through MCP,
// another browser) arrives on the account change stream
const memberThemeAccount = (userId: string, organization: Services['organization']): ThemeAccount => ({
  read: () => organization.fetchMemberPreferences(userId).then(preferences => (typeof preferences.theme === 'string' ? preferences.theme : undefined)),
  save: theme => organization.patchMemberPreferences(userId, { theme }),
  onChange: listener =>
    onAccountChanges(['orgMember'], events => {
      if (events.length > 0 && !events.some(event => event.entityUniqueId === userId)) return;
      invalidateMemberPreferences(userId);
      listener();
    }),
});

// The theme's account for whoever is signed in, and their [ ] keys: the theme before or after; with Shift, straight to the default light or dark
function ThemeHost() {
  const { user } = useAuthUser();
  const { organization } = useServices();
  const userId = user?.id;
  const account = useMemo(() => (userId && organization ? memberThemeAccount(userId, organization) : null), [userId, organization]);
  return (
    <>
      <ThemeAccountSync account={account} />
      {userId && BACKEND_ENVIRONMENT !== 'production' && <ThemeCycleKeys />}
    </>
  );
}

export default function App() {
  // Hide the initial HTML splash screen for non-editor pages
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isEditorPage = currentPath.startsWith('/editor/');
    
    // Only hide splash immediately if NOT on editor page
    // Editor page will handle its own splash screen via EditorLoadingScreen
    if (!isEditorPage) {
      const initialSplash = document.getElementById('initial-splash');
      if (initialSplash) {
        initialSplash.classList.add('hidden');
      }
    }
  }, []);

  return (
    <div className="bg-lureo-shade flex min-h-screen flex-col">
      <AuthProvider>
        <CacheBuster />
        <ServiceMonitorBoot />
        <Provider store={store}>
          <TooltipProvider disableHoverableContent>
            <ModalProvider>
              <ServiceProvider>
                <OrganizationProvider>
                  <PostHogAnalyticsBinder />
                  <PostHogConfigurator />
                  <OAuthAnalyticsTracker />
                  <AttributionSync />
                  <AccountHost />
                  {BACKEND_ENVIRONMENT !== 'production' && <ThemeRowsSync />}
                  <ThemeHost />
                  <EnvironmentPreferencesProvider>
                    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                      <main className="flex-1">
                        <AppRoutes />
                      </main>
                    </Router>
                  </EnvironmentPreferencesProvider>
                </OrganizationProvider>
              </ServiceProvider>
            </ModalProvider>
          </TooltipProvider>
        </Provider>
      </AuthProvider>
      <CookieBanner />
      <CookiePreferencesModal />
      <AppToaster />
    </div>
  );
}